import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * TenderBarChart
 *
 * Responsibility:
 * - render a simple responsive bar chart for Tender quote analysis;
 * - keep raw values numeric so Chart.js can calculate scales correctly;
 * - optionally format displayed values through valueFormatter.
 *
 * Important:
 * - valueFormatter is used only for visual labels/ticks/tooltips;
 * - chart data remains numeric.
 */
export default function TenderBarChart({ data, valueFormatter }) {
    const { t } = useTranslation();

    const labels = useMemo(() => Object.keys(data || {}), [data]);
    const values = useMemo(() => Object.values(data || {}), [data]);

    /**
     * Safe formatter for any value shown to the user.
     *
     * If the parent does not provide a formatter, the raw value is displayed.
     */
    const formatValue = useCallback(
        (value) => {
            if (typeof valueFormatter === "function") {
                return valueFormatter(value);
            }

            return value;
        },
        [valueFormatter]
    );

    const cssColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--color-red-dark")
        .trim();

    //============================================================================================================
    // Prepare the chart data structure
    //============================================================================================================
    const chartData = useMemo(
        () => ({
            labels,
            datasets: [
                {
                    label: t("Value"),
                    barPercentage: 0.4,
                    categoryPercentage: 0.6,
                    data: values,
                    backgroundColor: cssColor,
                },
            ],
        }),
        [labels, values, cssColor, t]
    );

    //============================================================================================================
    // Calculate the maximum value for the y-axis
    //============================================================================================================
    const maxValue = useMemo(() => {
        const numericValues = values
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value));

        return Math.max(...numericValues, 0);
    }, [values]);

    //============================================================================================================
    // Prepare the chart options
    //============================================================================================================
    const options = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    beginAtZero: true,
                    suggestedMax: maxValue < 10 ? 10 : undefined,
                    ticks: {
                        font: {
                            size: 12,
                            weight: "bold",
                        },

                        /**
                         * Formats the Y-axis values.
                         *
                         * Example:
                         * - raw value: 200
                         * - displayed value: "200 $"
                         */
                        callback: (value) => formatValue(value),
                    },
                },
                x: {
                    ticks: {
                        font: {
                            size: 12,
                            weight: "bold",
                        },
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: true,
                    callbacks: {
                        /**
                         * Formats the tooltip value.
                         *
                         * Example:
                         * - raw value: 200
                         * - displayed value: "Value: 200 $"
                         */
                        label: (context) => {
                            const label = context.dataset?.label || t("Value");
                            const value = context.parsed?.y ?? context.raw;

                            return `${label}: ${formatValue(value)}`;
                        },
                    },
                },
            },
            animation: {
                easing: "easeOutQuart",
                duration: 500,
            },
        }),
        [formatValue, maxValue, t]
    );

    //============================================================================================================
    // Render the chart
    //============================================================================================================
    return (
        <div style={{ height: "300px" }}>
            <Bar data={chartData} options={options} />
        </div>
    );
}

TenderBarChart.propTypes = {
    data: PropTypes.object.isRequired,

    /**
     * Optional display formatter.
     *
     * Used for:
     * - Y-axis ticks;
     * - tooltip values.
     *
     * Raw chart values must remain numeric.
     */
    valueFormatter: PropTypes.func,
};